import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SolarProductType } from "../../model/solar-product/solar-product-type";
import * as service from "../../service/solar-product-service";
import { FileFromObjectUrl } from "../../model/common/file-from-object-url";
import { RootState } from "../store";

export type SolarProductDetailsState = {
    editingProductId?: string
    currentStep: number
    error?: string
    firstStep: FirstStepOfSolarProductDetailsState
    secondStep: SecondStepOfSolarProductDetailsState
    thirdStep: ThirdStepOfSolarProductDetailsState
}

export type FirstStepOfSolarProductDetailsState = {
    productName: string
    productCapacity: number
    price: number
    activeDuration?: string
    packageDescription?: string
    batteryType?: string
    categoryIds :  string[]
}

export type SecondStepOfSolarProductDetailsState = {
    inverterType?: string
    warranty?: string
    batteryVoltage?: string
    type: SolarProductType
    quantityAvailableForSale?: string
}

export type ThirdStepOfSolarProductDetailsState = {
    costForBungalow: string
    costPerFloor: string
    existingImages?: FileFromObjectUrl[]
    images: FileFromObjectUrl[]
}

const prepareFormData = (productInfo: SolarProductDetailsState) => {
    const form = new FormData();
  
    form.append("productName", productInfo.firstStep.productName);
    form.append("productCapacity", productInfo.firstStep.productCapacity.toString());
    form.append("price", productInfo.firstStep.price.toString());
    productInfo.firstStep.categoryIds.forEach((id) => {
      form.append("categoryIds", id);
    });
    
    productInfo.firstStep.activeDuration && form.append("activeDuration", productInfo.firstStep.activeDuration);
    productInfo.firstStep.packageDescription && form.append("packageDescription", productInfo.firstStep.packageDescription);
    productInfo.firstStep.batteryType && form.append("batteryType", productInfo.firstStep.batteryType);
    productInfo.secondStep.inverterType && form.append("inverterType", productInfo.secondStep.inverterType);
    productInfo.secondStep.warranty && form.append("warranty", productInfo.secondStep.warranty);
    productInfo.secondStep.batteryVoltage && form.append("batteryVoltage", productInfo.secondStep.batteryVoltage);
    form.append("type", productInfo.secondStep.type);
    productInfo.secondStep.quantityAvailableForSale && form.append("quantityAvailableForSale", productInfo.secondStep.quantityAvailableForSale);
    form.append("installationCostForBungalow", productInfo.thirdStep.costForBungalow.toString());
    form.append("installationCostForFloorForStoryBuilding", productInfo.thirdStep.costPerFloor.toString());
  
    return form;
  };

  const addNewProduct = createAsyncThunk<void, void, { state: RootState }>(
    'solarProductDetails/addNewProduct',
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const productInfo = getState().solarProductDetails;
            const formData = prepareFormData(productInfo);
            
            // Append images to formData
            for (const image of productInfo.thirdStep.images) {
                const blob = await fetch(image.url).then(r => r.blob());
                const file = new File([blob], image.name, { type: blob.type });
                formData.append("images", file, file.name);
            }
            
            // Use the RTK Query endpoint
             await dispatch(
                service.solarProductEndpoints.endpoints.addProduct.initiate(formData)
            );
        
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to add product");
        }
    },
);

const editProduct = createAsyncThunk<void, void, { state: RootState }>(
    'solarProductDetails/editProduct',
    async (_, { getState, dispatch, rejectWithValue  }) => {
        try {
            const productInfo = getState().solarProductDetails;
            if (!productInfo.editingProductId) {
                throw new Error("No product ID provided for editing");
            }
            
            const formData = prepareFormData(productInfo);
            
            // Handle existing and new images
            const images = productInfo.thirdStep.images.filter(
                image => !productInfo.thirdStep.existingImages?.some(
                    existingImage => existingImage.url === image.url
                )
            );
            
            // Append new images to formData
            for (const image of images) {
                const blob = await fetch(image.url).then(r => r.blob());
                const file = new File([blob], image.name, { type: blob.type });
                formData.append("images", file, file.name);
            }
            
            // Use the RTK Query endpoint
             await dispatch(
                service.solarProductEndpoints.endpoints.editProduct.initiate({
                    id: productInfo.editingProductId,
                    formData
                })
            );
        } catch (error: any) {
             return rejectWithValue(error.message || "Failed to edit product");
        }
    },
);


const initialState: SolarProductDetailsState = {
    currentStep: 1,
    firstStep: {
        productName: '',
        productCapacity: 0,
        price: 0,
        activeDuration: '',
        packageDescription: '',
        batteryType: '',
        categoryIds: [],
    },
    secondStep: {
        inverterType: '',
        warranty: '',
        batteryVoltage: '',
        type: SolarProductType.STORAGE_ONLY,
        quantityAvailableForSale: ''
    },
    thirdStep: {
        costForBungalow: '',
        costPerFloor: '',
        images: [],
    }
}

export const solarProductDetailsSlice = createSlice({
    name: 'solarProductDetails',
    initialState: initialState,
    reducers: {
        setupSolarProductDetails: (state, action : PayloadAction<SolarProductDetailsState | undefined>) => {
            state.error = initialState.error;
            state.editingProductId = initialState.editingProductId;
            state.currentStep = initialState.currentStep;
            if (action.payload) {
                state.editingProductId = action.payload.editingProductId;
                state.firstStep = {
                    ...action.payload.firstStep
                };
                state.secondStep = {
                    ...action.payload.secondStep
                };
                state.thirdStep = {
                    ...action.payload.thirdStep
                };
            } else {
                state.firstStep = {
                    ...initialState.firstStep
                };
                state.secondStep = {
                    ...initialState.secondStep
                };
                state.thirdStep = {
                    ...initialState.thirdStep,
                    existingImages: [],
                };
            }
        },
        moveToNextStep: (state) => {
            if (state.currentStep < 3) {
                state.currentStep += 1;
            }
        },
        moveToPreviousStep: (state) => {
            if (state.currentStep > 1) {
                state.currentStep -= 1;
            }
        },
        submitFirstStep: (state, action) => {
            state.firstStep = action.payload;
            state.currentStep = 2;
        },
        submitSecondStep: (state, action) => {
            state.secondStep = action.payload;
            state.currentStep = 3;
        },
        submitThirdStep: (state, action) => {
            state.thirdStep = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(addNewProduct.fulfilled, (state) => {
            state.currentStep = initialState.currentStep;
            state.error = undefined;
            state.firstStep = initialState.firstStep;
            state.secondStep = initialState.secondStep;
            state.thirdStep = initialState.thirdStep;
        });
        builder.addCase(addNewProduct.rejected, (state, action) => {
            state.error = action.error.message;
        });
        builder.addCase(editProduct.fulfilled, (state) => {
            state.currentStep = initialState.currentStep;
            state.error = undefined;
            state.firstStep = initialState.firstStep;
            state.secondStep = initialState.secondStep;
            state.thirdStep = initialState.thirdStep;
        });
        builder.addCase(editProduct.rejected, (state, action) => {
            state.error = action.error.message;
        });
    }
});

export const { moveToNextStep, moveToPreviousStep, submitFirstStep, submitSecondStep, submitThirdStep, setupSolarProductDetails } = solarProductDetailsSlice.actions;

export { addNewProduct, editProduct };

export default solarProductDetailsSlice.reducer;